import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Building2,
  Palette,
  Save,
  Loader2,
  Key,
  Shield,
  CheckCircle2,
  Link2,
  Eye,
  EyeOff,
  Sparkles,
  Image,
  ChevronDown,
} from "lucide-react";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { useToast } from "../components/ui/ToastModal";
import { useAuditLog } from "../hooks/useAuditLog";
import { DashpivotLogo } from "../components/IntegrationLogos";
import { useBranding } from "../hooks/useBranding";
import { XeroIntegrationCard } from "../components/XeroIntegrationCard";
import { logError } from "../lib/logError";

// ─── Font Options ────────────────────────────────────────────
const FONT_OPTIONS = [
  "Inter", "Roboto", "Open Sans", "Poppins", "Nunito", "Lato",
  "Montserrat", "Raleway", "Work Sans", "DM Sans", "Plus Jakarta Sans",
  "Source Sans 3", "Outfit", "Sora", "Manrope", "Space Grotesk",
];

// ─── Types ───────────────────────────────────────────────────
interface OrgInfo {
  id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  settings: unknown;
}

// ─── Component ───────────────────────────────────────────────
export function OrgSettingsPage() {
  const { addToast } = useToast();
  const auditLog = useAuditLog();
  const { branding, refresh: refreshBranding } = useBranding();

  const [, setOrgInfo] = useState<OrgInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchParams] = useSearchParams();
  const initialTab = useMemo(() => {
    const t = searchParams.get('tab');
    if (t === 'integrations' || t === 'branding' || t === 'security') return t;
    return 'general' as const;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [activeTab, setActiveTab] = useState<"general" | "branding" | "security" | "integrations">(initialTab);

  // Integration state
  const [intLoading, setIntLoading] = useState(false);
  const [intSaving, setIntSaving] = useState(false);
  const [intClientId, setIntClientId] = useState("");
  const [intClientSecret, setIntClientSecret] = useState("");
  const [intScope, setIntScope] = useState("");
  const [intAuthUrl, setIntAuthUrl] = useState("https://auth.sitemate.com/oauth2/token");
  const [intApiBase, setIntApiBase] = useState("https://api.sitemate.com/openapi/dashpivot/v1");
  const [intIsActive, setIntIsActive] = useState(false);
  const [intExistingId, setIntExistingId] = useState<string | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  // Load preview fonts so the select options render in their actual typefaces
  useEffect(() => {
    const fontId = "preview-fonts";
    if (!document.getElementById(fontId)) {
      const families = FONT_OPTIONS.map(f => `family=${f.replace(/\s+/g, '+')}`).join('&');
      const link = document.createElement("link");
      link.id = fontId;
      link.rel = "stylesheet";
      link.href = `https://fonts.googleapis.com/css2?${families}&display=swap`;
      document.head.appendChild(link);
    }
  }, []);

  // Editable fields
  const [orgName, setOrgName] = useState("");
  const [orgSlug, setOrgSlug] = useState("");
  const [appName, setAppName] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [primaryColor, setPrimaryColor] = useState("#1e73be");
  const [secondaryColor, setSecondaryColor] = useState("#3b82f6");
  const [accentColor, setAccentColor] = useState("#06b6d4");
  const [fontFamily, setFontFamily] = useState("Inter");
  const [headerFont, setHeaderFont] = useState("Inter");
  const [bodyFont, setBodyFont] = useState("Inter");

  // Logo upload state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const miniFileInputRef = useRef<HTMLInputElement>(null);
  const [miniLogoFile, setMiniLogoFile] = useState<File | null>(null);
  const [miniLogoPreview, setMiniLogoPreview] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [showAiPrompt, setShowAiPrompt] = useState(false);
  const [suggestedBranding, setSuggestedBranding] = useState<{
    primary_color?: string;
    secondary_color?: string;
    accent_color?: string;
    header_font?: string;
    body_font?: string;
  } | null>(null);

  const orgId = requireOrganizationId();

  // ── Fetch org data ───────────────────────────────────────
  const fetchOrgData = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, slug, logo_url, settings")
        .eq("id", orgId)
        .single();

      if (error) throw error;

      setOrgInfo(data);
      setOrgName(data.name);
      setOrgSlug(data.slug);

      // Populate branding fields from hook
      if (branding) {
        setAppName(branding.app_name || "");
        setCompanyName(branding.company_name || "");
        setPrimaryColor(branding.primary_color || "#1e73be");
        setSecondaryColor(branding.secondary_color || "#3b82f6");
        setAccentColor(branding.accent_color || "#06b6d4");
        setFontFamily(branding.font_family || "Inter");
        const ts = (branding.theme_settings || {}) as Record<string, string>;
        setHeaderFont(ts.header_font || branding.font_family || "Inter");
        setBodyFont(ts.body_font || branding.font_family || "Inter");
        if (branding.logo_url) setLogoPreview(branding.logo_url);
        if (branding.logo_mini_url) setMiniLogoPreview(branding.logo_mini_url);
      }
    } catch (err) {
      logError("[OrgSettings] Error:", err);
      addToast({ type: "error", title: "Failed to load organization settings" });
    } finally {
      setLoading(false);
    }
  }, [orgId, branding, addToast]);

  useEffect(() => {
    fetchOrgData();
  }, [fetchOrgData]);

  // ── Save General ─────────────────────────────────────────
  const handleSaveGeneral = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("organizations")
        .update({ name: orgName.trim(), slug: orgSlug.trim().toLowerCase() })
        .eq("id", orgId);

      if (error) throw error;
      addToast({ type: "success", title: "Organization settings saved" });
      fetchOrgData();

      // Audit log, non-blocking
      auditLog.log({
        action: "org.settings_update",
        entity_type: "organization",
        entity_id: orgId,
        metadata: { name: orgName.trim(), slug: orgSlug.trim().toLowerCase() },
      });
    } catch (err) {
      logError("[OrgSettings] Save error:", err);
      addToast({ type: "error", title: "Failed to save settings" });
    } finally {
      setSaving(false);
    }
  };

  // ── Logo Upload ──────────────────────────────────────────
  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast({ type: "error", title: "Please select an image file" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast({ type: "error", title: "Image must be under 5MB" });
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setShowAiPrompt(true);
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!logoFile) return logoPreview; // Return existing URL
    setUploading(true);
    try {
      const ext = logoFile.name.split(".").pop() || "png";
      const filePath = `${orgId}/logo.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("org-assets")
        .upload(filePath, logoFile, { upsert: true, contentType: logoFile.type });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("org-assets").getPublicUrl(filePath);
      return urlData.publicUrl;
    } catch (err) {
      logError("[OrgSettings] Logo upload error:", err);
      addToast({ type: "error", title: "Failed to upload logo" });
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleMiniLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      addToast({ type: "error", title: "Please select an image file" });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      addToast({ type: "error", title: "Image must be under 5MB" });
      return;
    }
    setMiniLogoFile(file);
    setMiniLogoPreview(URL.createObjectURL(file));
  };

  const uploadMiniLogo = async (): Promise<string | null> => {
    if (!miniLogoFile) return miniLogoPreview;
    setUploading(true);
    try {
      const ext = miniLogoFile.name.split(".").pop() || "png";
      const filePath = `${orgId}/logo_mini.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from("org-assets")
        .upload(filePath, miniLogoFile, { upsert: true, contentType: miniLogoFile.type });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from("org-assets").getPublicUrl(filePath);
      return urlData.publicUrl;
    } catch (err) {
      logError("[OrgSettings] Mini Logo upload error:", err);
      addToast({ type: "error", title: "Failed to upload mini logo" });
      return null;
    } finally {
      setUploading(false);
    }
  };

  // ── AI Brand Analysis ───────────────────────────────────
  const rasterizeSVG = async (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const targetWidth = img.width || 1024;
        const targetHeight = img.height || 1024;
        canvas.width = Math.min(targetWidth, 1024);
        canvas.height = (targetHeight / targetWidth) * canvas.width;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get 2d context"));
          return;
        }
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");
        URL.revokeObjectURL(url);
        resolve(dataUrl.split(",")[1]);
      };
      img.onerror = reject;
      img.src = url;
    });
  };

  const handleAiAnalyze = async () => {
    if (!logoFile) return;
    setAnalyzing(true);
    setShowAiPrompt(false);
    setSuggestedBranding(null);
    try {
      // Upload logo first
      const uploadedUrl = await uploadLogo();
      if (!uploadedUrl) return;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let payload: any = { imageUrl: uploadedUrl };
      
      if (logoFile.type.includes("svg")) {
        const base64 = await rasterizeSVG(logoFile);
        payload = { imageBase64: base64, mimeType: "image/png" };
      }

      // Call Edge Function for AI analysis
      const { data, error } = await supabase.functions.invoke("analyze-brand-logo", {
        body: payload,
      });

      if (error) throw error;

      // The response has shape: { success: true, branding: { primary_color, ... } }
      const branding = data?.branding;
      if (branding) {
        setSuggestedBranding(branding);
      }

      addToast({ type: "success", title: "AI analysis complete. Review the suggested colors & fonts below" });
    } catch (err) {
      logError("[OrgSettings] AI analysis error:", err);
      addToast({ type: "error", title: "AI analysis failed. You can still configure colors manually." });
    } finally {
      setAnalyzing(false);
    }
  };

  // ── Save Branding ────────────────────────────────────────
  const handleSaveBranding = async () => {
    setSaving(true);
    try {
      // Upload logo if new file selected
      let finalLogoUrl = logoPreview;
      if (logoFile) {
        finalLogoUrl = await uploadLogo();
      }

      // Upload mini logo if new file selected
      let finalMiniLogoUrl = miniLogoPreview;
      if (miniLogoFile) {
        finalMiniLogoUrl = await uploadMiniLogo();
      }

      const themeSettings = {
        ...(branding.theme_settings || {}),
        header_font: headerFont,
        body_font: bodyFont,
      };

      const { error } = await supabase
        .from("org_branding")
        .upsert(
          {
            organization_id: orgId,
            app_name: appName.trim(),
            company_name: companyName.trim() || null,
            primary_color: primaryColor,
            secondary_color: secondaryColor,
            accent_color: accentColor,
            font_family: bodyFont || fontFamily || "Inter",
            logo_url: finalLogoUrl,
            logo_mini_url: finalMiniLogoUrl || finalLogoUrl,
            theme_settings: themeSettings,
          },
          { onConflict: "organization_id" }
        );

      if (error) throw error;
      setLogoFile(null); // Clear pending file
      setMiniLogoFile(null); // Clear pending file
      addToast({ type: "success", title: "Branding updated" });
      refreshBranding();

      // Audit log, non-blocking
      auditLog.log({
        action: "org.branding_update",
        entity_type: "organization",
        entity_id: orgId,
        metadata: {
          app_name: appName.trim(),
          company_name: companyName.trim() || null,
          primary_color: primaryColor,
          header_font: headerFont,
          body_font: bodyFont,
        },
      });
    } catch (err) {
      logError("[OrgSettings] Branding save error:", err);
      addToast({ type: "error", title: "Failed to save branding" });
    } finally {
      setSaving(false);
    }
  };

  // ── Tabs ─────────────────────────────────────────────────
  const TABS = [
    { id: "general" as const, label: "General", icon: Building2 },
    { id: "branding" as const, label: "Branding", icon: Palette },
    { id: "integrations" as const, label: "Integrations", icon: Link2 },
    { id: "security" as const, label: "Security & SSO", icon: Shield },
  ];

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "var(--spacing-3xl)" }}>
        <Loader2 size={32} style={{ animation: "spin 1s linear infinite", color: "var(--color-primary)" }} />
      </div>
    );
  }

  return (
    <div style={{ padding: "var(--spacing-xl)" }}>
      {/* Header */}
      <div style={{ marginBottom: "var(--spacing-xl)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", marginBottom: "var(--spacing-xs)" }}>
          <Building2 size={24} style={{ color: "var(--color-primary)" }} />
          <h1 style={{ margin: 0, fontSize: "var(--font-size-2xl)", fontWeight: 700, color: "var(--text-primary)" }}>
            Organization Settings
          </h1>
        </div>
        <p style={{ margin: 0, color: "var(--text-muted)", fontSize: "var(--font-size-sm)" }}>
          Manage your organization's configuration, branding, and security settings.
        </p>
      </div>

      {/* Tab Bar */}
      <div
        style={{
          display: "flex",
          gap: "var(--spacing-xs)",
          marginBottom: "var(--spacing-xl)",
          borderBottom: "1px solid var(--border-color)",
          paddingBottom: "var(--spacing-xs)",
        }}
      >
        {TABS.map((tab) => {
          const isActive = activeTab === tab.id;
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                padding: "var(--spacing-sm) var(--spacing-md)",
                border: "none",
                borderBottom: `2px solid ${isActive ? "var(--color-primary)" : "transparent"}`,
                background: "transparent",
                color: isActive ? "var(--color-primary)" : "var(--text-muted)",
                fontWeight: isActive ? 600 : 400,
                fontSize: "var(--font-size-sm)",
                cursor: "pointer",
                transition: "all 0.15s ease",
                marginBottom: -1,
              }}
            >
              <TabIcon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === "general" && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-lg)",
            padding: "var(--spacing-xl)",
          }}
        >
          <h2 style={{ margin: "0 0 var(--spacing-lg)", fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--text-primary)" }}>
            General Information
          </h2>

          <div style={{ display: "grid", gap: "var(--spacing-lg)", maxWidth: 520 }}>
            <SettingsField label="Organization Name" description="The display name for this organization">
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                style={inputStyle}
              />
            </SettingsField>

            <SettingsField label="Slug" description="URL-friendly identifier (e.g., 'seq-formwork')">
              <input
                type="text"
                value={orgSlug}
                onChange={(e) => setOrgSlug(e.target.value.replace(/[^a-z0-9-]/g, ""))}
                style={inputStyle}
              />
            </SettingsField>

            <SettingsField label="Organization ID" description="Unique identifier (read-only)">
              <input
                type="text"
                value={orgId}
                disabled
                style={{ ...inputStyle, opacity: 0.6, cursor: "not-allowed" }}
              />
            </SettingsField>
          </div>

          <div style={{ marginTop: "var(--spacing-xl)", display: "flex", gap: "var(--spacing-sm)" }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveGeneral}
              disabled={saving}
              style={{ display: "flex", alignItems: "center", gap: "var(--spacing-xs)" }}
            >
              {saving ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={16} />}
              Save Changes
            </button>
          </div>
        </div>
      )}

      {activeTab === "branding" && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-lg)",
            padding: "var(--spacing-xl)",
          }}
        >
          <h2 style={{ margin: "0 0 var(--spacing-lg)", fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--text-primary)" }}>
            White-Label Branding
          </h2>

          {/* Live Sidebar Preview */}
          <div style={{ display: "flex", gap: "var(--spacing-xl)", marginBottom: "var(--spacing-xl)", flexWrap: "wrap" }}>
            {/* Mini sidebar mockup */}
            <div
              style={{
                width: 220,
                borderRadius: 12,
                overflow: "hidden",
                boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
                flexShrink: 0,
              }}
            >
              <div style={{ background: primaryColor, padding: 16, display: "flex", flexDirection: "column", height: 280 }}>
                {/* Logo + brand */}
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid rgba(255,255,255,0.12)" }}>
                  {logoPreview ? (
                    <img src={logoPreview} alt="Logo" style={{ width: 32, height: 32, borderRadius: 6, objectFit: "contain", background: "rgba(255,255,255,0.12)", padding: 2 }} />
                  ) : (
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 12 }}>
                      {(appName || "C")[0]}
                    </div>
                  )}
                  <div>
                    <div style={{ color: "#fff", fontWeight: 700, fontSize: 12, fontFamily: `'${headerFont}', sans-serif` }}>{appName || "Claims App"}</div>
                    <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>{companyName || "Your Company"}</div>
                  </div>
                </div>

                {/* Nav mockup items */}
                {["Dashboard", "Projects", "Activity"].map((item, i) => (
                  <div
                    key={item}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 10px", borderRadius: 8, marginBottom: 3,
                      background: i === 0 ? "rgba(255,255,255,0.14)" : "transparent",
                      color: i === 0 ? "#fff" : "rgba(255,255,255,0.7)",
                      fontSize: 12, fontWeight: i === 0 ? 600 : 500,
                      fontFamily: `'${bodyFont}', sans-serif`,
                    }}
                  >
                    <div style={{ width: 16, height: 16, borderRadius: 4, background: i === 0 ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)" }} />
                    {item}
                  </div>
                ))}

                {/* Bottom */}
                <div style={{ marginTop: "auto", paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.1)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 10, color: "rgba(255,255,255,0.5)" }}>
                    <div style={{ width: 24, height: 24, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 10 }}>E</div>
                    <span>Admin User</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Color swatches summary */}
            <div style={{ flex: 1, minWidth: 280 }}>
              <p style={{ fontSize: "var(--font-size-sm)", color: "var(--text-secondary)", marginBottom: 16 }}>
                The sidebar preview shows how your brand colors will appear in the navigation. Upload your logo and let AI suggest matching colors and fonts.
              </p>
              <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: primaryColor, border: "2px solid rgba(0,0,0,0.1)" }} />
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Primary</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: secondaryColor, border: "2px solid rgba(0,0,0,0.1)" }} />
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Secondary</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 8, background: accentColor, border: "2px solid rgba(0,0,0,0.1)" }} />
                  <span style={{ fontSize: 10, color: "var(--text-muted)" }}>Accent</span>
                </div>
              </div>
              <div style={{ display: "flex", gap: 16, fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
                <span>Heading: <strong style={{ fontFamily: `'${headerFont}', sans-serif` }}>{headerFont}</strong></span>
                <span>Body: <strong style={{ fontFamily: `'${bodyFont}', sans-serif` }}>{bodyFont}</strong></span>
              </div>
            </div>
          </div>

          {/* Logos Upload Section */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--spacing-md)", marginBottom: "var(--spacing-xl)" }}>
            {/* Full Logo */}
            <div style={{
              border: "2px dashed var(--border-color)",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--spacing-lg)",
              textAlign: "center",
              background: "var(--bg-primary)",
              cursor: "pointer",
              transition: "border-color 200ms",
            }}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = primaryColor; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "var(--border-color)";
                const file = e.dataTransfer.files[0];
                if (file) {
                  const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                  handleLogoSelect(fakeEvent);
                }
              }}
            >
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleLogoSelect} style={{ display: "none" }} />
              {logoPreview ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--spacing-md)" }}>
                  <img src={logoPreview} alt="Logo preview" style={{ width: 64, height: 64, objectFit: "contain", borderRadius: 8, background: "#f8fafc", padding: 4 }} />
                  <div style={{ textAlign: "left" }}>
                    <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: 0, fontSize: "var(--font-size-sm)" }}>Full Logo uploaded</p>
                    <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: "var(--font-size-xs)" }}>Click to replace</p>
                  </div>
                </div>
              ) : (
                <>
                  <Image size={32} style={{ color: "var(--text-muted)", marginBottom: 8 }} />
                  <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 4px" }}>Upload Full Logo</p>
                  <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "var(--font-size-xs)" }}>
                    Used when sidebar expanded
                  </p>
                </>
              )}
            </div>

            {/* Mini Logo */}
            <div style={{
              border: "2px dashed var(--border-color)",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--spacing-lg)",
              textAlign: "center",
              background: "var(--bg-primary)",
              cursor: "pointer",
              transition: "border-color 200ms",
            }}
              onClick={() => miniFileInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.borderColor = primaryColor; }}
              onDragLeave={(e) => { e.currentTarget.style.borderColor = "var(--border-color)"; }}
              onDrop={(e) => {
                e.preventDefault();
                e.currentTarget.style.borderColor = "var(--border-color)";
                const file = e.dataTransfer.files[0];
                if (file) {
                  const fakeEvent = { target: { files: [file] } } as unknown as React.ChangeEvent<HTMLInputElement>;
                  handleMiniLogoSelect(fakeEvent);
                }
              }}
            >
              <input ref={miniFileInputRef} type="file" accept="image/*" onChange={handleMiniLogoSelect} style={{ display: "none" }} />
              {miniLogoPreview ? (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "var(--spacing-md)" }}>
                  <img src={miniLogoPreview} alt="Mini logo preview" style={{ width: 64, height: 64, objectFit: "contain", borderRadius: 8, background: "#f8fafc", padding: 4 }} />
                  <div style={{ textAlign: "left" }}>
                    <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: 0, fontSize: "var(--font-size-sm)" }}>Mini Logo uploaded</p>
                    <p style={{ color: "var(--text-muted)", margin: "4px 0 0", fontSize: "var(--font-size-xs)" }}>Click to replace</p>
                  </div>
                </div>
              ) : (
                <>
                  <Image size={32} style={{ color: "var(--text-muted)", marginBottom: 8 }} />
                  <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 4px" }}>Upload Mini Logo</p>
                  <p style={{ color: "var(--text-muted)", margin: 0, fontSize: "var(--font-size-xs)" }}>
                    Used when sidebar collapsed
                  </p>
                </>
              )}
            </div>
          </div>

          {/* AI Analysis Prompt */}
          {showAiPrompt && (
            <div style={{
              background: "linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 100%)",
              border: "1px solid #c7d2fe",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--spacing-lg)",
              marginBottom: "var(--spacing-xl)",
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
            }}>
              <Sparkles size={24} style={{ color: "#6366f1", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 4px", fontSize: "var(--font-size-sm)" }}>
                  Let AI analyze your logo?
                </p>
                <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "var(--font-size-xs)" }}>
                  AI will extract colors and suggest fonts from your logo. This uses your organization's AI credits.
                </p>
              </div>
              <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                <button className="btn btn-secondary" onClick={() => setShowAiPrompt(false)} style={{ fontSize: "var(--font-size-xs)" }}>
                  Skip
                </button>
                <button className="btn btn-primary" onClick={handleAiAnalyze} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--font-size-xs)" }}>
                  <Sparkles size={14} /> Analyze
                </button>
              </div>
            </div>
          )}

          {/* Analyzing indicator */}
          {analyzing && (
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
              padding: "var(--spacing-lg)",
              background: "var(--bg-primary)",
              borderRadius: "var(--border-radius-md)",
              marginBottom: "var(--spacing-xl)",
              border: "1px solid var(--border-color)",
            }}>
              <Loader2 size={20} style={{ animation: "spin 1s linear infinite", color: "#6366f1" }} />
              <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-sm)" }}>
                AI is analyzing your logo…
              </span>
            </div>
          )}

          {/* AI Suggestions */}
          {suggestedBranding && (
            <div style={{
              background: "linear-gradient(135deg, #f0f7ff 0%, #f5f0ff 100%)",
              border: "1px solid #c7d2fe",
              borderRadius: "var(--border-radius-md)",
              padding: "var(--spacing-lg)",
              marginBottom: "var(--spacing-xl)",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-md)", marginBottom: "var(--spacing-md)" }}>
                <Sparkles size={24} style={{ color: "#6366f1", flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 600, color: "var(--text-primary)", margin: "0 0 4px", fontSize: "var(--font-size-sm)" }}>
                    AI Recommended Branding
                  </p>
                  <p style={{ color: "var(--text-secondary)", margin: 0, fontSize: "var(--font-size-xs)" }}>
                    Review the suggestions below. You can apply them to the form, or discard them.
                  </p>
                </div>
                <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                  <button className="btn btn-secondary" onClick={() => setSuggestedBranding(null)} style={{ fontSize: "var(--font-size-xs)" }}>
                    Discard
                  </button>
                  <button className="btn btn-primary" onClick={() => {
                    if (suggestedBranding.primary_color) setPrimaryColor(suggestedBranding.primary_color);
                    if (suggestedBranding.secondary_color) setSecondaryColor(suggestedBranding.secondary_color);
                    if (suggestedBranding.accent_color) setAccentColor(suggestedBranding.accent_color);
                    if (suggestedBranding.header_font) setHeaderFont(suggestedBranding.header_font);
                    if (suggestedBranding.body_font) setBodyFont(suggestedBranding.body_font);
                    setSuggestedBranding(null);
                    addToast({ type: "success", title: "Recommendations applied. Click 'Save Changes' below to confirm." });
                  }} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "var(--font-size-xs)" }}>
                    Apply Recommendations
                  </button>
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: "var(--spacing-md)", background: "white", padding: "var(--spacing-md)", borderRadius: "var(--border-radius-sm)", border: "1px solid var(--border-color)" }}>
                {suggestedBranding.primary_color && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", marginBottom: 4 }}>Primary</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: suggestedBranding.primary_color, border: "1px solid var(--border-color)" }} />
                      <span style={{ fontSize: "var(--font-size-sm)", fontFamily: "monospace" }}>{suggestedBranding.primary_color}</span>
                    </div>
                  </div>
                )}
                {suggestedBranding.secondary_color && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", marginBottom: 4 }}>Secondary</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: suggestedBranding.secondary_color, border: "1px solid var(--border-color)" }} />
                      <span style={{ fontSize: "var(--font-size-sm)", fontFamily: "monospace" }}>{suggestedBranding.secondary_color}</span>
                    </div>
                  </div>
                )}
                {suggestedBranding.accent_color && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", marginBottom: 4 }}>Accent</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 24, height: 24, borderRadius: "50%", background: suggestedBranding.accent_color, border: "1px solid var(--border-color)" }} />
                      <span style={{ fontSize: "var(--font-size-sm)", fontFamily: "monospace" }}>{suggestedBranding.accent_color}</span>
                    </div>
                  </div>
                )}
                {suggestedBranding.header_font && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", marginBottom: 4 }}>Header Font</div>
                    <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 500, fontFamily: `"${suggestedBranding.header_font}", sans-serif` }}>{suggestedBranding.header_font}</div>
                  </div>
                )}
                {suggestedBranding.body_font && (
                  <div>
                    <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-secondary)", marginBottom: 4 }}>Body Font</div>
                    <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 400, fontFamily: `"${suggestedBranding.body_font}", sans-serif` }}>{suggestedBranding.body_font}</div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ display: "grid", gap: "var(--spacing-lg)", maxWidth: 520 }}>
            <SettingsField label="App Name" description="The name shown in the sidebar and page titles">
              <input
                type="text"
                value={appName}
                onChange={(e) => setAppName(e.target.value)}
                placeholder="e.g., Claims Pro"
                style={inputStyle}
              />
            </SettingsField>

            <SettingsField label="Company Name" description="Your company name for branding">
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="e.g., Acme Corp"
                style={inputStyle}
              />
            </SettingsField>

            {/* Color Pickers */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "var(--spacing-md)" }}>
              <SettingsField label="Primary" description="Sidebar & buttons">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={{ width: 36, height: 36, border: "none", borderRadius: 6, cursor: "pointer", padding: 0 }}
                  />
                  <input
                    type="text"
                    value={primaryColor}
                    onChange={(e) => setPrimaryColor(e.target.value)}
                    style={{ ...inputStyle, flex: 1, fontFamily: "monospace", fontSize: "var(--font-size-xs)" }}
                  />
                </div>
              </SettingsField>

              <SettingsField label="Secondary" description="Accents & links">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    style={{ width: 36, height: 36, border: "none", borderRadius: 6, cursor: "pointer", padding: 0 }}
                  />
                  <input
                    type="text"
                    value={secondaryColor}
                    onChange={(e) => setSecondaryColor(e.target.value)}
                    style={{ ...inputStyle, flex: 1, fontFamily: "monospace", fontSize: "var(--font-size-xs)" }}
                  />
                </div>
              </SettingsField>

              <SettingsField label="Accent" description="Highlights & badges">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    style={{ width: 36, height: 36, border: "none", borderRadius: 6, cursor: "pointer", padding: 0 }}
                  />
                  <input
                    type="text"
                    value={accentColor}
                    onChange={(e) => setAccentColor(e.target.value)}
                    style={{ ...inputStyle, flex: 1, fontFamily: "monospace", fontSize: "var(--font-size-xs)" }}
                  />
                </div>
              </SettingsField>
            </div>

            {/* Typography: Header + Body fonts */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--spacing-md)" }}>
              <SettingsField label="Heading Font" description="For titles and headers">
                <FontSelect 
                  value={headerFont}
                  onChange={setHeaderFont}
                  options={FONT_OPTIONS}
                />
              </SettingsField>

              <SettingsField label="Body Font" description="For general text">
                <FontSelect 
                  value={bodyFont}
                  onChange={setBodyFont}
                  options={FONT_OPTIONS}
                />
              </SettingsField>
            </div>
          </div>

          <div style={{ marginTop: "var(--spacing-xl)", display: "flex", gap: "var(--spacing-sm)" }}>
            <button
              className="btn btn-primary"
              onClick={handleSaveBranding}
              disabled={saving || uploading}
              style={{ display: "flex", alignItems: "center", gap: "var(--spacing-xs)" }}
            >
              {saving || uploading ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={16} />}
              {uploading ? "Uploading…" : saving ? "Saving…" : "Save Branding"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "integrations" && (
        <>
        <IntegrationsTab
          orgId={orgSlug}
          loading={intLoading}
          saving={intSaving}
          clientId={intClientId}
          clientSecret={intClientSecret}
          scope={intScope}
          authUrl={intAuthUrl}
          apiBase={intApiBase}
          isActive={intIsActive}
          existingId={intExistingId}
          showSecret={showSecret}
          setClientId={setIntClientId}
          setClientSecret={setIntClientSecret}
          setScope={setIntScope}
          setAuthUrl={setIntAuthUrl}
          setApiBase={setIntApiBase}
          setShowSecret={setShowSecret}
          onLoad={async () => {
            setIntLoading(true);
            try {
              const orgId = await requireOrganizationId();
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const { data } = await (supabase.from as any)("organization_integrations")
                .select("id, config, is_active, vault_secret_id")
                .eq("organization_id", orgId)
                .eq("integration_type", "dashpivot")
                .single();
              if (data) {
                setIntExistingId(data.id);
                setIntIsActive(data.is_active);
                const cfg = data.config || {};
                setIntScope(cfg.scope || "");
                setIntAuthUrl(cfg.auth_url || "https://auth.sitemate.com/oauth2/token");
                setIntApiBase(cfg.api_base_url || "https://api.sitemate.com/openapi/dashpivot/v1");
                // Don't load actual secrets to the frontend, show placeholder
                if (data.vault_secret_id) {
                  setIntClientId("••••••••");
                  setIntClientSecret("••••••••");
                }
              }
            } catch { /* no integration yet */ }
            setIntLoading(false);
          }}
          onSave={async () => {
            setIntSaving(true);
            try {
              const orgId = await requireOrganizationId();
              const credentials = { client_id: intClientId, client_secret: intClientSecret, scope: intScope };
              const config = { scope: intScope, auth_url: intAuthUrl, api_base_url: intApiBase };

              if (intExistingId) {
                // Update existing, use Vault RPC
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await (supabase.from as any)("organization_integrations")
                  .update({ config, updated_at: new Date().toISOString() })
                  .eq("id", intExistingId);
                // Only update credentials if user changed them (not the masked placeholder)
                if (!intClientId.startsWith("••")) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  await supabase.rpc("store_integration_credentials" as any, {
                    p_integration_id: intExistingId,
                    p_credentials: credentials,
                  });
                }
                auditLog.log({ action: "integration.update", metadata: { type: "dashpivot", organization_id: orgId } });
              } else {
                // Insert new integration
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const { data: newInt, error } = await (supabase.from as any)("organization_integrations")
                  .insert({
                    organization_id: orgId,
                    integration_type: "dashpivot",
                    credentials: { _pending_vault: true },
                    config,
                    is_active: true,
                  })
                  .select("id")
                  .single();
                if (error) throw error;
                // Encrypt via Vault
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                await supabase.rpc("store_integration_credentials" as any, {
                  p_integration_id: newInt.id,
                  p_credentials: credentials,
                });
                setIntExistingId(newInt.id);
                setIntIsActive(true);
                auditLog.log({ action: "integration.create", metadata: { type: "dashpivot", organization_id: orgId } });
              }
              addToast({ type: "success", title: "Dashpivot integration saved" });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
              addToast({ type: "error", title: "Failed to save integration", message: err.message });
            }
            setIntSaving(false);
          }}
        />
        {/* Xero Integration */}
        <div style={{ marginTop: 'var(--spacing-lg)' }}>
          <XeroIntegrationCard />
        </div>
        </>
      )}

      {activeTab === "security" && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border-color)",
            borderRadius: "var(--border-radius-lg)",
            padding: "var(--spacing-xl)",
          }}
        >
          <h2 style={{ margin: "0 0 var(--spacing-lg)", fontSize: "var(--font-size-lg)", fontWeight: 600, color: "var(--text-primary)" }}>
            Security & Single Sign-On
          </h2>

          {/* SSO Status Card */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--spacing-md)",
              padding: "var(--spacing-lg)",
              background: "rgba(16,185,129,0.06)",
              border: "1px solid rgba(16,185,129,0.15)",
              borderRadius: "var(--border-radius-md)",
              marginBottom: "var(--spacing-lg)",
            }}
          >
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "rgba(16,185,129,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#10b981",
                flexShrink: 0,
              }}
            >
              <CheckCircle2 size={20} />
            </div>
            <div>
              <div style={{ fontWeight: 600, color: "var(--text-primary)", fontSize: "var(--font-size-sm)" }}>
                Azure AD SSO: Active
              </div>
              <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
                Single Sign-On is configured via Azure Active Directory. All team members sign in using their organizational Microsoft accounts.
              </div>
            </div>
          </div>

          {/* Security Features */}
          <div style={{ display: "grid", gap: "var(--spacing-md)" }}>
            <SecurityRow
              label="Row Level Security (RLS)"
              status="active"
              description="All database tables enforce organization-level data isolation"
            />
            <SecurityRow
              label="Organization Data Isolation"
              status="active"
              description="Each organization's data is fully isolated via RLS policies"
            />
            <SecurityRow
              label="Role-Based Access Control"
              status="active"
              description="4-tier role hierarchy: Super Admin → Admin → Member → Viewer"
            />
            <SecurityRow
              label="Platform Admin Separation"
              status="active"
              description="Developer access is separated from client admin via is_platform_admin flag"
            />
          </div>

          {/* Info Box */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "var(--spacing-sm)",
              marginTop: "var(--spacing-xl)",
              padding: "var(--spacing-md)",
              background: "rgba(59,130,246,0.06)",
              border: "1px solid rgba(59,130,246,0.15)",
              borderRadius: "var(--border-radius-md)",
              fontSize: "var(--font-size-xs)",
              color: "var(--text-muted)",
            }}
          >
            <Key size={16} style={{ color: "#3b82f6", flexShrink: 0, marginTop: 1 }} />
            <div>
              <p style={{ margin: 0, fontWeight: 600, color: "#3b82f6" }}>SSO Configuration</p>
              <p style={{ margin: "2px 0 0" }}>
                SSO is configured at the Supabase Auth level. To add additional identity providers
                for new client organizations, update the Supabase Dashboard → Authentication → Providers.
                Each tenant can have their own Azure AD / Google Workspace configuration.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Integrations Tab Component ───────────────────────────────
function IntegrationsTab(props: {
  orgId: string;
  loading: boolean; saving: boolean;
  clientId: string; clientSecret: string; scope: string;
  authUrl: string; apiBase: string; isActive: boolean;
  existingId: string | null; showSecret: boolean;
  setClientId: (v: string) => void; setClientSecret: (v: string) => void;
  setScope: (v: string) => void; setAuthUrl: (v: string) => void;
  setApiBase: (v: string) => void; setShowSecret: (v: boolean) => void;
  onLoad: () => void; onSave: () => void;
}) {
  useEffect(() => { props.onLoad(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (props.loading) return <div style={{ display: "flex", justifyContent: "center", padding: "var(--spacing-3xl)" }}><Loader2 size={24} style={{ animation: "spin 1s linear infinite", color: "var(--color-primary)" }} /></div>;

  return (
    <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-color)", borderRadius: "var(--border-radius-lg)", padding: "var(--spacing-xl)", paddingTop: "calc(var(--spacing-xl) + 3px)", position: "relative", overflow: "hidden" }}>
      {/* Brand accent bar */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: 3,
        background: "linear-gradient(90deg, #2E81DA 0%, #1B5FAA 60%, #144D8C 100%)",
        borderRadius: "var(--border-radius-lg) var(--border-radius-lg) 0 0",
      }} />

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "var(--spacing-lg)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-md)" }}>
          <div style={{ filter: "drop-shadow(0 2px 6px rgba(46, 129, 218, 0.25))" }}>
            <DashpivotLogo size={42} />
          </div>
          <div>
            <h2 style={{
              margin: 0, fontSize: "var(--font-size-lg)", fontWeight: 700,
              color: "var(--text-primary)",
              display: "flex", alignItems: "center", gap: 8,
            }}>
              Dashpivot
              <span style={{
                fontSize: 11, fontWeight: 500, padding: "2px 8px",
                borderRadius: 4, background: "rgba(46, 129, 218, 0.08)",
                color: "#2E81DA", letterSpacing: "0.02em",
              }}>Variations</span>
            </h2>
            <p style={{ margin: "3px 0 0", fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>Connect your Sitemate Dashpivot account to sync variations automatically.</p>
          </div>
        </div>
        {props.existingId && (
          <span style={{
            fontSize: 11, fontWeight: 600, padding: "4px 12px", borderRadius: 99,
            background: props.isActive ? "rgba(16,185,129,0.08)" : "rgba(245,158,11,0.08)",
            color: props.isActive ? "#10b981" : "#f59e0b",
            border: `1px solid ${props.isActive ? "rgba(16,185,129,0.2)" : "rgba(245,158,11,0.2)"}`,
            display: "flex", alignItems: "center", gap: 5,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: props.isActive ? "#10b981" : "#f59e0b",
              boxShadow: props.isActive ? "0 0 6px rgba(16,185,129,0.5)" : "0 0 6px rgba(245,158,11,0.5)",
            }} />
            {props.isActive ? "ACTIVE" : "INACTIVE"}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gap: "var(--spacing-md)" }}>
        <SettingsField label="Client ID" description="OAuth2 Client ID from Sitemate Developer Portal">
          <input style={inputStyle} value={props.clientId} onChange={e => props.setClientId(e.target.value)} placeholder="Enter Client ID" onFocus={() => { if (props.clientId.startsWith("••")) props.setClientId(""); }} />
        </SettingsField>
        <SettingsField label="Client Secret" description="OAuth2 Client Secret (encrypted via Vault)">
          <div style={{ position: "relative" }}>
            <input style={inputStyle} type={props.showSecret ? "text" : "password"} value={props.clientSecret} onChange={e => props.setClientSecret(e.target.value)} placeholder="Enter Client Secret" onFocus={() => { if (props.clientSecret.startsWith("••")) props.setClientSecret(""); }} />
            <button onClick={() => props.setShowSecret(!props.showSecret)} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", padding: 4 }}>
              {props.showSecret ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </SettingsField>
        <SettingsField label="Scope" description="Target entity scope (UUID)">
          <input style={inputStyle} value={props.scope} onChange={e => props.setScope(e.target.value)} placeholder="63067314-10ed-4e4f-b122-5dfbf75a33d2" />
        </SettingsField>
        <SettingsField label="Auth URL" description="OAuth2 token endpoint">
          <input style={inputStyle} value={props.authUrl} onChange={e => props.setAuthUrl(e.target.value)} />
        </SettingsField>
        <SettingsField label="API Base URL" description="Dashpivot OpenAPI endpoint">
          <input style={inputStyle} value={props.apiBase} onChange={e => props.setApiBase(e.target.value)} />
        </SettingsField>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", marginTop: "var(--spacing-xl)" }}>
        <button className="btn btn-primary" onClick={props.onSave} disabled={props.saving} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          {props.saving ? <Loader2 size={16} style={{ animation: "spin 1s linear infinite" }} /> : <Save size={16} />}
          {props.existingId ? "Update Integration" : "Connect Dashpivot"}
        </button>
        <span style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
          <Key size={12} style={{ display: "inline", verticalAlign: "middle", marginRight: 4 }} />
          Credentials are encrypted at rest via Supabase Vault
        </span>
      </div>
    </div>
  );
}

// ── Shared Styles ──────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: "100%",
  height: 40,
  padding: "0 var(--spacing-sm)",
  border: "1px solid var(--border-color)",
  borderRadius: "var(--border-radius-md)",
  background: "var(--bg-secondary)",
  color: "var(--text-primary)",
  fontSize: "var(--font-size-sm)",
  transition: "border-color 0.15s ease",
};

// ── Custom Font Select ─────────────────────────────────────
function FontSelect({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} style={{ position: "relative" }}>
      <div 
        onClick={() => setOpen(!open)}
        style={{
          ...inputStyle, 
          display: "flex", 
          alignItems: "center", 
          justifyContent: "space-between",
          cursor: "pointer",
          fontFamily: `'${value}', sans-serif`,
          userSelect: "none"
        }}
      >
        <span>{value}</span>
        <ChevronDown size={16} />
      </div>
      {open && (
        <div style={{
          position: "absolute",
          top: "100%",
          left: 0,
          right: 0,
          marginTop: 4,
          background: "var(--bg-card)",
          border: "1px solid var(--border-color)",
          borderRadius: "var(--border-radius-sm)",
          boxShadow: "var(--shadow-md)",
          maxHeight: 250,
          overflowY: "auto",
          zIndex: 50
        }}>
          {options.map(f => (
            <div
              key={f}
              onClick={() => {
                onChange(f);
                setOpen(false);
              }}
              style={{
                padding: "8px 12px",
                cursor: "pointer",
                fontFamily: `'${f}', sans-serif`,
                background: f === value ? "var(--bg-hover)" : "transparent",
                color: "var(--text-primary)"
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "var(--bg-hover)"}
              onMouseLeave={(e) => e.currentTarget.style.background = f === value ? "var(--bg-hover)" : "transparent"}
            >
              {f}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Reusable Settings Field ────────────────────────────────
function SettingsField({
  label,
  description,
  children,
}: {
  label: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label
        style={{
          display: "block",
          fontSize: "var(--font-size-sm)",
          fontWeight: 500,
          color: "var(--text-primary)",
          marginBottom: 2,
        }}
      >
        {label}
      </label>
      {description && (
        <p
          style={{
            margin: "0 0 var(--spacing-xs)",
            fontSize: "var(--font-size-xs)",
            color: "var(--text-muted)",
          }}
        >
          {description}
        </p>
      )}
      {children}
    </div>
  );
}

// ── Security Status Row ────────────────────────────────────
function SecurityRow({
  label,
  status,
  description,
}: {
  label: string;
  status: "active" | "inactive";
  description: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--spacing-md)",
        padding: "var(--spacing-sm) var(--spacing-md)",
        border: "1px solid var(--border-color)",
        borderRadius: "var(--border-radius-md)",
      }}
    >
      <CheckCircle2
        size={16}
        style={{ color: status === "active" ? "#10b981" : "var(--text-muted)", flexShrink: 0 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: "var(--font-size-sm)", fontWeight: 500, color: "var(--text-primary)" }}>
          {label}
        </div>
        <div style={{ fontSize: "var(--font-size-xs)", color: "var(--text-muted)" }}>
          {description}
        </div>
      </div>
      <span
        style={{
          fontSize: "var(--font-size-xs)",
          fontWeight: 600,
          color: status === "active" ? "#10b981" : "#ef4444",
          padding: "2px 8px",
          borderRadius: 99,
          background: status === "active" ? "rgba(16,185,129,0.10)" : "rgba(239,68,68,0.10)",
        }}
      >
        {status === "active" ? "Active" : "Inactive"}
      </span>
    </div>
  );
}
